<script lang="ts">
import { defineComponent, PropType } from 'vue'

import { formatTime, localTime, UtcOffsetDisplay } from '../util'
import { TimeSlot, Store, Artist } from '../types'

export default defineComponent({
  name: 'TimeSlotItem',
  props: {
    timezone: {
      type: String,
      default: '',
    },
    emitter: {
      type: Object,
      required: true,
    },
    store: {
      type: Object as PropType<Store>,
      required: true,
    },
    item: {
      type: Object as PropType<TimeSlot>,
      required: true,
    },

    selected: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const getArtistById = (artistId: string): Artist => {
      const artistIndex = props.store.artistList.findIndex(
        artist => artist.id === parseInt(artistId),
      )
      return props.store.artistList[artistIndex]
    }

    const selectSlot = (slotId: number): void => {
      if (props.item.artistId) {
        props.emitter.emit('select-slot', slotId)
      }
    }

    const toggleSlot = (): void => {
      if (props.item.artistId) {
        if (props.selected) {
          props.emitter.emit('select-slot', -1)
        } else {
          props.emitter.emit('select-slot', props.item.id)
        }
      }
    }

    const setTz = ({ tz, offset }: { tz: string; offset: number }): void => {
      props.emitter.emit('set-tz', {
        tz,
        offset,
      })
    }

    const clearSlot = (): void => {
      if (props.item.artistId) {
        props.emitter.emit('clear-slot', props.item.id)
      }
    }

    const setLockStatus = (status: string): void => {
      props.emitter.emit('set-slot-status', { slotId: props.item.id, status: status })
    }

    return {
      formatTime,
      localTime,
      props,
      getArtistById,
      selectSlot,
      UtcOffsetDisplay,
      clearSlot,
      toggleSlot,
      setLockStatus,
      setTz,
    }
  },
})
</script>

<template>
  <div>
    <div class="px-4 py-4 sm:px-6 sm:flex items-center justify-between">
      <div v-if="item.artistId" class="w-20 cursor-pointer mr-4" @click="toggleSlot">
        <img :src="getArtistById(item.artistId).photoUrl" class="w-16 h-16 rounded-full" />
      </div>
      <div class="w-full">
        <div class="flex items-center justify-between">
          <div class="text-sm leading-5 font-medium text-slipmat-600 truncate">
            <i class="fas fa-headphones mr-2"></i>
            <span v-if="item.artistId" @click="toggleSlot" class="cursor-pointer">{{
              getArtistById(item.artistId).name
            }}</span>
            <span v-else>Unreserved</span>
          </div>
          <div class="ml-2 flex-shrink-0 flex">
            <span
              class="px-2 inline-flex text-xs leading-normal font-semibold rounded-full  text-gray-800 bg-gray-100 cursor-pointer"
              title="Festival timezone. Click to set."
              @click="setTz({ tz: 'UTC', offset: 0 })"
            >
              {{ formatTime(localTime(item.time, 'UTC')) }}
            </span>

            <span
              v-if="item.artistId"
              class="ml-2 px-2 inline-flex text-xs leading-normal font-semibold rounded-full text-slipmat-700"
              :class="{ 'bg-slipmat-100': item.status === 'TENTATIVE' }"
            >
              <template v-if="item.status === 'TENTATIVE'">
                TENTATIVE
              </template>

              <template v-if="item.status === 'CONFIRMED'">
                <i class="far fa-lock-alt leading-normal mr-1"></i> CONFIRMED
              </template>
            </span>
          </div>
        </div>
        <div class="mt-2 sm:flex sm:flex-col">
          <div class="sm:flex sm:justify-between">
            <div class="sm:flex">
              <div
                v-if="item.artistId"
                @click="
                  setTz({
                    tz: getArtistById(item.artistId).timezone,
                    offset: getArtistById(item.artistId).utcOffset,
                  })
                "
                title="Click to set timezone."
                class="mr-6 flex items-center text-sm leading-normal text-gray-500 cursor-pointer"
              >
                <i class="far fa-clock mr-1 leading-normal"></i>
                {{ formatTime(localTime(item.time, getArtistById(item.artistId).timezone)) }}
                - {{ getArtistById(item.artistId).timezone }} ({{
                  UtcOffsetDisplay(getArtistById(item.artistId).utcOffset)
                }})
              </div>
              <!-- <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mt-0">
                <svg
                  class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Remote
              </div> -->
            </div>
            <div class="mt-2 flex items-center text-sm leading-normal text-gray-500 sm:mt-0">
              {{ formatTime(localTime(item.time, props.timezone)) }} - {{ props.timezone }}
            </div>
          </div>
          <div
            v-show="!item.artistId"
            class="mt-2 sm:flex sm:justify-between w-full items-center text-sm leading-normal text-gray-500"
          >
            <div>&nbsp;</div>

            <div class="w-full max-w-xs border-dashed border-2 border-gray-300 rounded-md">
              <div class="w-full max-w-xs pt-2 pb-5" :id="'slot-' + item.id">
                &nbsp;
              </div>
              <div class="w-full max-w-xs h-2"></div>
            </div>
          </div>
        </div>
        <div
          class="mt-2 sm:flex items-center justify-between items-center text-xs leading-5 text-gray-400"
          v-if="item.artistId"
        >
          <div>
            <a
              :href="getArtistById(item.artistId).profileUrl"
              target="_blank"
              class="cursor-pointer hover:text-slipmat-700"
            >
              <i class="far fa-link"></i>
              <span class="pl-1 pr-2">Profile</span>
            </a>

            <i class="far fa-tags pr-1"></i>
            <span v-for="(tag, index) in getArtistById(item.artistId).tags" :key="index"
              >{{ tag }}<span v-if="index < getArtistById(item.artistId).tags.length - 1">, </span>
            </span>
          </div>
          <div
            v-if="!props.selected"
            @click="toggleSlot"
            class="cursor-pointer hover:text-slipmat-700"
          >
            <i class="far fa-edit"></i>
          </div>
        </div>
      </div>
    </div>
    <div v-if="props.selected" class="border-b border-slipmat-400">
      <div class="border-t border-dashed border-gray-300">
        <div class="px-4 py-4 sm:px-6 flex justify-between items-center">
          <div>
            <button
              v-if="item.status !== 'CONFIRMED'"
              class="w-28 py-1 px-2 shadow border bg-indigo-600 rounded-md text-white text-sm hover:shadow-none hover:bg-indigo-500 mr-4"
              @click="clearSlot"
            >
              <i class="far fa-minus-circle mr-1"></i>
              Remove
            </button>

            <button
              v-if="item.status === 'TENTATIVE'"
              class="w-28 mr-4 py-1 px-2 shadow border bg-indigo-600 rounded-md text-white text-sm hover:shadow-none hover:bg-indigo-500"
              @click="setLockStatus('CONFIRMED')"
            >
              <i class="far fa-lock-alt mr-1"></i>
              Confirm
            </button>

            <button
              v-if="item.status === 'CONFIRMED'"
              class="w-28 mr-4 py-1 px-2 shadow border bg-indigo-600 rounded-md text-white text-sm hover:shadow-none hover:bg-indigo-500"
              @click="setLockStatus('TENTATIVE')"
            >
              <i class="far fa-lock-open-alt mr-1"></i>
              Unconfirm
            </button>
          </div>

          <span
            class="text-slipmat-700 text-sm cursor-pointer leading-normal"
            @click="selectSlot(-1)"
            ><i class="far fa-times mr-1"></i> Close</span
          >
        </div>
      </div>
    </div>
  </div>
</template>
